/** @jsx jsx */

import { useContext, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import EditMarker from 'components/UI/EditMarker';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { useSelector, connect } from 'react-redux';
import { InteractionContext } from '../../Layouts/ContainerLogical';
import checkButtonColors from 'services/checkButtonColors';
import getAllInteractions from 'services/apiHelpers/getAllInteractions';
import isLastInteraction from 'services/apiHelpers/isLastInteraction';
import getBlockById from 'services/apiHelpers/getBlockById';
import DButton from './DButton';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import LastPageMessage from 'components/Editor/EditorUtilities/UI/LastPageMessage';
import hasValidLocationUpdated from 'services/hasValidLocationUpdated';
import { useMemo } from 'react';
import { NavigationButton } from 'components/lib/Buttons';

const { lPink } = colors;

interface IEditable {
  isEditActive: boolean;
  children: any;
  lockDown: boolean;
  unlocked?: boolean;
  id: string;
  shape?: string;
  background?: string;
  foreground?: string;
  label?: string;
  color?: string;
  borderWidth?: string;
  borderColor?: string;
  localBorderColor?: boolean;
  buttonShape?: string;
  localShape?: boolean;
  borderRadius?: number | string;
  localBorderRadius?: boolean;
  localButtonOutline?: boolean;
  buttonOutline?: boolean;
  useLargeButton?: boolean;
  localUseLargeButton?: boolean;
  buttonShadow?: boolean;
  localShadow?: boolean;
  font?: string;
  location?: string;
  style?: {};
  hasSetRemoveable?: boolean;
  removeable?: boolean;
  focusColor?: string;
  disableLastPageMessage?: boolean;
  enabled?: boolean;
  buttonHover?: string;
  localButtonHover?: boolean;
  linkData?: { [key: string]: string };
}

const ContinueBtn = (props: IEditable) => {
  const {
    isEditActive,
    id,
    label,
    font,
    unlocked,
    disableLastPageMessage,
    focusColor,
  } = props as any;

  // might not need this just yet
  // button is ' disabled ' by isLocked on interaction context
  let { enabled } = props;
  // so we can map it something else if need be

  let { location } = props;

  const {
    buttonShape,
    buttonShadow,
    buttonOutline,
    useLargeButton,
    borderColor,
    buttonBorderRadius = 0,
    buttonHover,
  } = useSelector((state: any) => {
    // props.localShape props.localShadow
    // if this is set. means author has made a decision on the button props
    // we preference this setting true or false

    const {
      buttonShape,
      buttonShadow,
      buttonOutline,
      useLargeButton,
      buttonBorderColor,
      buttonBorderRadius,
      buttonHover,
    } = state.theme;

    return {
      buttonShape: props.localShape ? props.buttonShape : buttonShape,
      buttonShadow: props.localShadow ? props.buttonShadow : buttonShadow,
      buttonOutline: props.localButtonOutline
        ? props.buttonOutline
        : buttonOutline,
      useLargeButton: props.localUseLargeButton
        ? props.useLargeButton
        : useLargeButton,
      borderColor: props.localBorderColor
        ? props.borderColor
        : buttonBorderColor,
      buttonBorderRadius: props.localBorderRadius
        ? props.borderRadius
        : buttonBorderRadius,
      buttonHover: props.localButtonHover ? props.buttonHover : buttonHover,
    };
  });

  const { background, color } = checkButtonColors(props);

  const interactionContext = useContext(InteractionContext) as any;

  const exit = useMemo(() => {
    if (!window.dev()) {
      return;
    }
    // console.log('checking for if to display isLastInteractionMessage');
    //
    return (
      getAllInteractions().length > 1 &&
      location !== 'exit' &&
      isLastInteraction(interactionContext.id)
    );
  }, []);

  useEffect(() => {
    // this is the hack to get this onto the legacy buttons
    // would want to set it from the factory but that would only work for newly created buttons
    //
    if (props.hasOwnProperty('removeable')) {
      const { hasSetRemoveable, removeable } = props;
      if (!hasSetRemoveable) {
        setBlockProps(getBlockById(id), { hasSetRemoveable: true, removeable });
      }
    }
  }, []);

  // error checking here against a potentially removed topic
  let hasValidLocation = hasValidLocationUpdated(location);

  if (props?.linkData && props?.linkData.url) {
    location = props.linkData.url;
    hasValidLocation = true;
  }

  let buttonBlock = findByKeyVal_V2(
    interactionContext.id,
    'type',
    'Column'
  ) as any;

  const buttonCount =
    buttonBlock.meta &&
    buttonBlock.meta.subType === 'ButtonBlock' &&
    buttonBlock.children.length;

  return window.dev() ? (
    <div
      id={`continue-btn-${id}`}
      css={css`
        position: relative;
        flex: 0,
        padding: 2px 3px 2px 3px;
        box-shadow: ${
          isEditActive
            ? `0 0 0 1px ${lPink}`
            : `0 0 0 1px ${hasValidLocation ? 'lightGrey' : 'yellow'}`
        };
        transition: box-shadow 0.2s;
        cursor: pointer;
      `}
      onClick={e => {
        e.stopPropagation();
        if (isEditActive) {
          return;
        }
        setCurrentBlock({ id });
      }}
    >
      <DButton
        enabled={enabled}
        id={id}
        shape={buttonShape}
        background={background}
        foreground={color}
        borderWidth={buttonOutline ? 1 : 0}
        borderColor={background}
        buttonHover={buttonHover}
        buttonOutline={buttonOutline}
        useLargeButton={useLargeButton}
        borderRadius={buttonBorderRadius}
        buttonShadow={buttonShadow}
        font={font}
        location={location}
        focusColor={focusColor}
      >
        {label}
      </DButton>
      {!isEditActive && <EditMarker />}

      {/* can only have the one button. else assumption is made author knows what they're up to */}
      {buttonCount === 1 && exit && !disableLastPageMessage && (
        <LastPageMessage />
      )}
    </div>
  ) : (
    <DButton
      enabled={enabled}
      borderWidth={buttonOutline ? 1 : 0}
      borderColor={background}
      font={font}
      location={hasValidLocation ? location : 'next'}
      focusColor={focusColor}
      unlocked={unlocked}
      {...{
        ...props,
        background,
        foreground: color,
        shape: buttonShape,
        borderRadius: buttonBorderRadius,
        buttonShadow,
        buttonOutline,
        useLargeButton,
        buttonHover,
      }}
    >
      {label}
    </DButton>
  );
};

export default NavigationButton;
